/**
 * Scrolls to a DOM element by id, preferring Toolpad's `main` / nearest
 * scrollable ancestor over `window` (window often doesn't move in DashboardLayout).
 *
 * Retries after layout settles — listing pages often grow after the first paint,
 * which otherwise leaves the target too high in the viewport.
 */
export function scrollToElementById(
  id: string,
  options?: {
    /** Extra space above the target (sticky app bar). */
    offsetPx?: number;
    behavior?: ScrollBehavior;
  },
): boolean {
  const el = document.getElementById(id);
  if (!el) return false;

  const offsetPx = options?.offsetPx ?? 72;
  const behavior = options?.behavior ?? 'smooth';

  const scrollContainer = resolveScrollContainer(el);
  const containerRect = scrollContainer.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const nextTop = scrollContainer.scrollTop + (elRect.top - containerRect.top) - offsetPx;
  scrollContainer.scrollTo({ top: Math.max(0, nextTop), behavior });
  return true;
}

function resolveScrollContainer(el: HTMLElement): HTMLElement {
  const main = document.querySelector('main');
  if (main instanceof HTMLElement && main.contains(el)) {
    return main;
  }

  let scrollParent: HTMLElement | null = el.parentElement;
  while (scrollParent && scrollParent !== document.body) {
    const style = window.getComputedStyle(scrollParent);
    const overflowY = style.overflowY;
    const canScrollY =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      scrollParent.scrollHeight > scrollParent.clientHeight + 1;
    if (canScrollY) {
      return scrollParent;
    }
    scrollParent = scrollParent.parentElement;
  }

  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/** Smooth first pass, then instant corrections as layout settles. */
export function scrollToElementByIdWithRetries(
  id: string,
  options?: { offsetPx?: number; delaysMs?: number[] },
): () => void {
  const delays = options?.delaysMs ?? [80, 250, 600, 1200];
  const timers = delays.map((delay, index) =>
    window.setTimeout(() => {
      scrollToElementById(id, {
        offsetPx: options?.offsetPx,
        behavior: index === 0 ? 'smooth' : 'auto',
      });
    }, delay),
  );
  return () => timers.forEach((timer) => window.clearTimeout(timer));
}
